type StorageType = "local" | "session";

class Storage {
  private getStorage(type: StorageType): globalThis.Storage | null {
    if (typeof window === "undefined") return null;

    try {
      return type === "local" ? window.localStorage : window.sessionStorage;
    } catch {
      return null;
    }
  }

  set<T>(
    key: string,
    value: T,
    type: StorageType = "local",
    expiresIn?: number
  ): boolean {
    const storage = this.getStorage(type);
    if (!storage) return false;

    try {
      const data = {
        value,
        timestamp: Date.now(),
        expiresIn,
      };
      storage.setItem(key, JSON.stringify(data));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * ttlOverride: kayıt yazılırken expiresIn belirtilmemiş olsa bile (eski kayıtlar)
   * okuma anında bu süreyi zorunlu kılar.
   */
  get<T>(
    key: string,
    type: StorageType = "local",
    ttlOverride?: number
  ): T | null {
    const storage = this.getStorage(type);
    if (!storage) return null;

    try {
      const item = storage.getItem(key);
      if (!item) return null;

      const data = JSON.parse(item);
      const effectiveTtl = data.expiresIn ?? ttlOverride;

      if (effectiveTtl) {
        const elapsed = Date.now() - data.timestamp;
        if (elapsed > effectiveTtl) {
          this.remove(key, type);
          return null;
        }
      }

      return data.value;
    } catch {
      return null;
    }
  }

  remove(key: string, type: StorageType = "local"): boolean {
    const storage = this.getStorage(type);
    if (!storage) return false;

    try {
      storage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  clear(type: StorageType = "local"): boolean {
    const storage = this.getStorage(type);
    if (!storage) return false;

    try {
      storage.clear();
      return true;
    } catch {
      return false;
    }
  }

  has(key: string, type: StorageType = "local"): boolean {
    const storage = this.getStorage(type);
    if (!storage) return false;

    try {
      return storage.getItem(key) !== null;
    } catch {
      return false;
    }
  }

  /** Kaydın yazıldığı anı döner (data.value değil, sarmalayıcının kendi timestamp'i) */
  getRecordTimestamp(key: string, type: StorageType = "local"): number | null {
    const storage = this.getStorage(type);
    if (!storage) return null;

    try {
      const item = storage.getItem(key);
      if (!item) return null;

      const data = JSON.parse(item);
      return typeof data.timestamp === "number" ? data.timestamp : null;
    } catch {
      return null;
    }
  }

  setWithTimestamp(key: string, type: StorageType = "local"): boolean {
    return this.set(key, Date.now(), type);
  }

  getTimestamp(key: string, type: StorageType = "local"): number | null {
    return this.get<number>(key, type);
  }

  getElapsedTime(key: string, type: StorageType = "local"): number | null {
    const timestamp = this.getTimestamp(key, type);
    if (!timestamp) return null;

    return Date.now() - timestamp;
  }

  isExpired(
    key: string,
    duration: number,
    type: StorageType = "local"
  ): boolean {
    const elapsed = this.getElapsedTime(key, type);
    if (elapsed === null) return true;

    return elapsed > duration;
  }
}

export const storage = new Storage();

export const STORAGE_KEYS = {
  WEB_VISIT_LOGGED: "web_visit_logged",
  /** Uygulama indirme sheet'i kapatıldı — kalıcı, TTL yok (bkz. AppDownloadSheet). */
  APP_PROMO_DISMISSED: "app_promo_dismissed",
} as const;

export const STORAGE_DURATIONS = {
  MINUTES_30: 30 * 60 * 1000,
  HOUR_1: 60 * 60 * 1000,
  HOURS_2: 2 * 60 * 60 * 1000,
  DAY_1: 24 * 60 * 60 * 1000,
  WEEK_1: 7 * 24 * 60 * 60 * 1000,
} as const;